import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";

import type { AdvertisingConsentChoice } from "@/lib/advertisingConsent";
import type { SessionUserSnapshot } from "@/lib/auth-server-fns";
import { QUERY_STALE_TIMES } from "@/utils/query-policies";
import type { ViewerPreferences } from "@garimpa/lib/viewerPreferences";

export interface ViewerCapabilities {
  telegramAuthAvailable: boolean;
  whatsappAuthAvailable: boolean;
}

export interface ViewerClickTracking {
  origin: string;
  context: string | null;
}

interface ViewerContextBase {
  clickTracking: ViewerClickTracking | null;
  advertisingConsent: AdvertisingConsentChoice | null;
  attribution: Record<string, string>;
  preferences: ViewerPreferences;
  capabilities: ViewerCapabilities;
  isBot: boolean;
  isHuman: boolean;
  now: string;
}

export interface GuestViewerContext extends ViewerContextBase {
  user: SessionUserSnapshot;
  userId: string;
  identity: "guest";
  isAuthenticated: true;
  isVisitor: false;
  isGuest: true;
  isMember: false;
}

export interface MemberViewerContext extends ViewerContextBase {
  user: SessionUserSnapshot;
  userId: string;
  identity: "member";
  isAuthenticated: true;
  isVisitor: false;
  isGuest: false;
  isMember: true;
}

export interface VisitorViewerContext extends ViewerContextBase {
  user: null;
  userId: null;
  identity: "visitor";
  isAuthenticated: false;
  isVisitor: true;
  isGuest: false;
  isMember: false;
}

export type ViewerContext = GuestViewerContext | MemberViewerContext | VisitorViewerContext;

export function createViewerContext(input: {
  clickTracking?: ViewerClickTracking | null;
  advertisingConsent?: AdvertisingConsentChoice | null;
  attribution?: Record<string, string>;
  user: SessionUserSnapshot | null;
  preferences: ViewerPreferences;
  capabilities: ViewerCapabilities;
  isBot: boolean;
  now: string;
}): ViewerContext {
  const common = {
    clickTracking: input.clickTracking ?? null,
    advertisingConsent: input.advertisingConsent ?? null,
    attribution: input.attribution ?? {},
    preferences: input.preferences,
    capabilities: input.capabilities,
    isBot: input.isBot,
    isHuman: !input.isBot,
    now: input.now,
  };

  if (input.user) {
    if (input.user.isAnonymous) {
      return {
        ...common,
        user: input.user,
        userId: input.user.id,
        identity: "guest",
        isAuthenticated: true,
        isVisitor: false,
        isGuest: true,
        isMember: false,
      };
    }

    return {
      ...common,
      user: input.user,
      userId: input.user.id,
      identity: "member",
      isAuthenticated: true,
      isVisitor: false,
      isGuest: false,
      isMember: true,
    };
  }

  return {
    ...common,
    user: null,
    userId: null,
    identity: "visitor",
    isAuthenticated: false,
    isVisitor: true,
    isGuest: false,
    isMember: false,
  };
}

export const VIEWER_CONTEXT_QUERY_KEY = ["viewer", "context"] as const;

export const getViewerContext = createServerFn({ method: "GET" }).handler(
  async (): Promise<ViewerContext> => {
    const { resolveViewerContext } = await import("./viewer-context.server");
    return resolveViewerContext();
  },
);

export function viewerContextQueryOptions() {
  return queryOptions({
    queryKey: VIEWER_CONTEXT_QUERY_KEY,
    queryFn: () => getViewerContext(),
    staleTime: QUERY_STALE_TIMES.session,
  });
}

export function getViewerContextForRoute(queryClient: QueryClient): Promise<ViewerContext> {
  return queryClient.ensureQueryData(viewerContextQueryOptions());
}

export function getViewerContextForGuard(
  queryClient: QueryClient | undefined,
): Promise<ViewerContext> {
  if (!queryClient) return getViewerContext();

  const cached = queryClient.getQueryData<ViewerContext>(VIEWER_CONTEXT_QUERY_KEY);
  if (cached) return Promise.resolve(cached);

  return queryClient.fetchQuery(viewerContextQueryOptions());
}

function setViewerContextQueryData(
  queryClient: QueryClient,
  updater: ViewerContext | ((current: ViewerContext | undefined) => ViewerContext | undefined),
) {
  queryClient.setQueryData(VIEWER_CONTEXT_QUERY_KEY, updater);
}

export function patchViewerContextQueryData(
  queryClient: QueryClient,
  patch: {
    preferences?: Partial<ViewerContext["preferences"]>;
    user?: Partial<NonNullable<ViewerContext["user"]>> | null;
  },
) {
  setViewerContextQueryData(queryClient, (current) => {
    if (!current) return current;

    const user =
      patch.user === undefined
        ? current.user
        : patch.user === null
          ? null
          : current.user
            ? { ...current.user, ...patch.user }
            : current.user;

    return createViewerContext({
      clickTracking: patch.user === undefined ? current.clickTracking : null,
      advertisingConsent: current.advertisingConsent,
      user,
      attribution: patch.user === null ? {} : current.attribution,
      preferences: patch.preferences
        ? { ...current.preferences, ...patch.preferences }
        : current.preferences,
      capabilities: current.capabilities,
      isBot: current.isBot,
      now: current.now,
    });
  });
}

export async function invalidateViewerContext(queryClient: QueryClient) {
  await queryClient.cancelQueries({ queryKey: VIEWER_CONTEXT_QUERY_KEY });
  setViewerContextQueryData(queryClient, (current) =>
    current ? { ...current, clickTracking: null } : current,
  );
  return queryClient.invalidateQueries({ queryKey: VIEWER_CONTEXT_QUERY_KEY });
}
